import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import cookies from 'js-cookie';
import endpoints from '~settings/endpoints';
import * as queries from '~settings/queries';
import { cookieNames } from '~constants';
import { getItem } from '~helpers/storage';
import { message } from 'antd';
import debounce from 'lodash/debounce';

export const useTableColumnSearch = props => {
  const {
    searchType = '',
    valueData = '',
    disabled = false,
    isLocationCustom = false,
    showCountry = false,
    basicType = '',
    t = () => {},
    saveSearchValue = () => {},
    resetValue = () => {},
    changeCurrencyState = () => {},
    publishedStatus = 'ALL',
    onSearch = () => {},
    onChange = () => {},
  } = props;

  const PAGE_SIZE = 20;
  const [results, setResults] = useState([]);
  const [value, setValue] = useState(valueData);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const isIdOneOfSIds = value => results.find(result => result.id === value);

  const loadMoreResults = () => {
    if (hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchSearchData(value, nextPage);
    }
  };

  const parseResults = (response, searchType) => {
    const data = response.data.data.search.edges;
    return data.map(item => {
      const result = {
        slug: item.node.slug,
        name: item.node.name,
        id: item.node.id,
      };

      switch (searchType) {
        case 'landlord':
          result.bookingJourney = item.node.bookingJourney;
          result.published = true;
          break;
        case 'city':
          result.countryName = item.node.country ? item.node.country.name : null;
          result.published = item.node.published;
          result.currency = item.node.country.currencyCode;
          result.billingCycle = item.node.country.billingCycle;
          break;
        default:
          result.countryName = item.node.country ? item.node.country.name : null;
          result.published = item.node.published;
          break;
      }
      return result;
    });
  };

  const fetchSearchData = async (value, page = 1) => {
    try {
      const authorization = `Bearer ${cookies.get(cookieNames.token)}`;
      const headers = {
        Authorization: authorization,
        'Accept-Language': 'en-us',
      };

      const authPayload = getItem('PMS_CURRENT_USER_AUTH');
      if (authPayload && authPayload.payload && authPayload.payload.currentRoleSlug) {
        headers['Current-Role'] = authPayload.payload.currentRoleSlug;
      }
      axios({
        method: 'post',
        url: endpoints.search.url(searchType),
        data: queries.search(
          { query: value, publishedStatus: publishedStatus, pageNumber: page, pageSize: PAGE_SIZE },
          searchType,
        ),
        headers,
      })
        .then(response => {
          if (!response.data.errors) {
            if (!response.data?.data?.search?.edges?.length) {
              setResults(null);
              setHasMore(false);
              return;
            }
            const newResults = parseResults(response, searchType);
            setResults(prevResults => (page === 1 ? newResults : [...prevResults, ...newResults]));
            setHasMore(newResults.length === 20);
          } else {
            message.error(t('cms.message.error'));
          }
        })
        .catch(() => {
          message.error(t('cms.message.error'));
        });
    } catch (error) {
      message.error(error);
    }
  };

  const debouncedFetchSearchData = useCallback(debounce(fetchSearchData, 500), []);

  const handleSearch = newValue => {
    if (value !== newValue) {
      setPage(1);
    }

    setValue(newValue);

    if (saveSearchValue) {
      saveSearchValue(newValue, searchType);
    }

    if (resetValue) {
      resetValue(searchType);
    }

    if (!newValue) {
      changeCurrencyState(false);
    }

    if (!newValue) {
      return;
    }

    if (results && isIdOneOfSIds(newValue)) {
      return;
    }

    debouncedFetchSearchData(newValue);
  };

  const handleSelect = value => {
    if (isLocationCustom) {
      const search = results ? results.find(result => result.id === value) : {};
      onSearch(search.slug, search, searchType);
      if (showCountry) {
        setValue(search.slug ? `${search.name}, ${search.countryName}` : '');
      } else {
        setValue(search.name ? search.name : '');
      }
      if (basicType === 'basicCity') {
        changeCurrencyState(true, search);
      }
    } else {
      const decodedValue = window.atob(value);
      const valueId = JSON.parse(decodedValue).id;
      onSearch(valueId, decodedValue);
      setValue(value);
    }
  };

  const handleInput = e => {
    setValue(e.target.value);
  };

  const handleScroll = e => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      loadMoreResults();
    }
  };

  useEffect(
    prev => {
      if (valueData !== prev?.valueData) {
        setValue(valueData);
      }
    },
    [valueData],
  );

  useEffect(() => {
    if (valueData && !disabled) {
      handleSearch(valueData);
    }
  }, []);

  useEffect(() => {
    onChange.length && onChange(value);
  }, [value]);

  return { value, results, handleSearch, handleSelect, handleInput, handleScroll };
};
